import {
  ActionIcon,
  Badge,
  ComboboxItem,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { paginationBase } from "../../../_base/model/_base/BaseTable";
import { _localization } from "../../../config/location";
import { DeliveryListModel } from "../../../model/TblDeliveryAssignment";
import {
  getDeliveryStatusSelect,
  getTblLocationSelect,
} from "../../../service/getSelectApi";
import { green } from "../../../const/variables";
import { styleTableMantine } from "../../../_base/_const/_constVar";
import useDynamicHeight from "../../../_base/component/Core/hookBase";
import { IconReload } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { IconCoin } from "@tabler/icons-react";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";

const DeliveryList = ({ dataDetail, isSetHeight }: DeliveryListProps) => {
  //data and fetching state
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const height = useDynamicHeight(490);

  //table state
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [pagination, setPagination] = useState(paginationBase);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "soCtGoc",
        header: "Số hóa đơn",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="lg"
            style={{ cursor: "pointer" }}
            color={renderedCellValue == null ? "red" : green.base}
          >
            {renderedCellValue ?? "N/A"}
          </Badge>
        ),
      },
      {
        accessorKey: "maCt",
        header: "Mã vận đơn",
        Cell: ({ renderedCellValue }) => (
          <Badge
            radius="sm"
            variant="dot"
            size="lg"
            style={{ cursor: "pointer" }}
            color={renderedCellValue == null ? "red" : green.base}
          >
            {renderedCellValue ?? "N/A"}
          </Badge>
        ),
      },
      {
        accessorKey: "ngayPc",
        header: "Ngày phân công",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "tenBp",
        header: "Bộ phận giao hàng",
      },
      {
        accessorKey: "tenNguoiGh",
        header: "Tên nhân viên giao hàng",
      },
      {
        accessorKey: "modifiedStatusName",
        header: "Trạng thái đơn giao hàng",
        Cell: ({ row }) => (
          <Badge color={getColorStatus(row.original.modifiedStatusName)}>
            {row.original.modifiedStatusName}
          </Badge>
        ),
      },
      {
        accessorKey: "receptionTime",
        header: "Ngày tiếp nhận",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "deliveryTime",
        header: "Ngày giao hàng",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "arrivalTime",
        header: "Ngày đến nơi",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "completionTime",
        header: "Ngày hoàn thành",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "ngayHd",
        header: "Ngày giờ xuất kho",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="sm">
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
    ],
    [dataDetail]
  );
  function getColorStatus(value: string) {
    switch (value) {
      case "Đã phân công":
        return "lime";
      case "Lập đơn giao hàng":
        return "green";
      case "Đang giao hàng":
        return "teal";
      case "Đơn hàng giao chưa thành công do Khách hàng":
        return "blue";
      case "Đơn hàng giao chưa thành công do Hacom":
        return "indigo";
      case "Hàng đã giao":
        return "violet";
      case "Hủy phân công":
        return "red";
      case "Đã đến nơi":
        return "#2bdd66";
      case "Nối chuyến":
        return "yellow";
      case "Hủy đơn":
        return "#c91a25";
      case null:
        return "rgba(0, 0, 0, 0)";
    }
  }
  const table = useMantineReactTable({
    columns,
    data: dataDetail,
    enableColumnResizing: true,
    positionToolbarAlertBanner: "bottom",
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["maCt"],
        right: ["action"],
      },
      columnVisibility: { id: false, "mrt-row-select": false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: {
        maxHeight: isSetHeight ? height : 500,
        minHeight: isSetHeight ? height : 100,
      },
    },
    enableStickyHeader: true,
    // onRowSelectionChange: setRowSelection,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableTopToolbar: false,
    rowCount,
    enablePagination: false,
    enableBottomToolbar: false,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    state: {
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      style: {
        width: "1400px", // Đặt chiều rộng cố định
        tableLayout: "fixed", // Đảm bảo bảng không tự động co giãn
      },
      striped: false,
    },
  });

  return (
    <>
      <MantineReactTable table={table} />
    </>
  );
};
type DeliveryListProps = {
  dataDetail: any;
  isSetHeight: any;
};

export default DeliveryList;
